import { PiCheckBold } from "react-icons/pi";
import routes from "@/routers/routes";
import { Badge, Button, cn, Select, Title } from "rizzui";
import PageHeader from "@/app/shared/page-header";
import FooterSection from "@/app/shared/orders/details/addresses";
import TrackingHistory from "@/app/shared/orders/details/tracking-history";
import ShippingDetails from "@/app/shared/orders/details/shipping-details";
import { metaObject } from "@/config/site.config";
import { useParams } from "react-router-dom";
import { useContext, useEffect, useState } from "react";
import {
    addPurchaseOrder,
    addShipment,
    fetchCategories,
    fetchOrderById,
    rejectOrder,
    updateOrderStatus,
} from "@/server/api-services";
import {
    Category,
    Order,
    OrderStatus,
    PurchaseOrder,
    Reject,
    Shipment,
} from "@/types";
import OrderSection from "@/app/shared/orders/details/delivery-details";
import ModalButton from "@/app/shared/modal-button";
import {
    AddPurchaseOrderModal,
    AddShipmentModal,
} from "@/app/shared/orders/details/add-Modal";
import toast from "react-hot-toast";
import DetailsSection from "@/app/shared/orders/details/details-section";
import BillingTable from "@/app/shared/orders/details/billings";
import { AuthContext } from "@/auth/authContext";
import { permissions } from "@/app/shared/roles-permissions/permissions";
import { RejectModal } from "@/app/shared/orders/details/reject-Modal";

export const metadata = {
    ...metaObject("Shipment Details"),
};

export default function OrderDetails() {
    const { id } = useParams();
    const { role } = useContext(AuthContext);

    const [order, setOrder] = useState({} as Order);

    const [categories, setCategories] = useState<Category[]>([]);

    const [currentOrderStatus, setCurrentOrderStatus] = useState(0);

    const pageHeader = {
        title: "Order Details ",
        breadcrumb: [
            {
                href: routes.eCommerce.dashboard,
                name: "Home",
            },
            {
                href: routes.orders.list,
                name: "Orders",
            },
            {
                name: "Details",
            },
            {
                name: "#" + order.orderId,
            },
        ],
    };

    const orderStatus = [
        { id: 0, label: OrderStatus.Requested },
        { id: 1, label: OrderStatus.Processing },
        { id: 2, label: OrderStatus.Purchased },
        { id: 3, label: OrderStatus.InTransit },
        { id: 4, label: OrderStatus.Delivered },
    ];

    const getCategories = async () => {
        const response = await fetchCategories();
        if (response.statusCode !== 200) {
            toast.error("Failed to fetch order");
            return;
        }

        var data = response.data as Category[];

        setCategories(data);
    };

    useEffect(() => {
        getOrder();
        getCategories();
    }, [id]);

    const getOrder = async () => {
        if (!id) return;

        const response = await fetchOrderById(id as string);

        if (response.statusCode !== 200) {
            return;
        }

        var data = response.data as Order;

        data = {
            ...data,
            status: OrderStatus[data.status as keyof typeof OrderStatus],
        };

        setCurrentOrderStatus(
            orderStatus.findIndex((x) => x.label === data.status),
        );

        setOrder(data);
    };

    const handleUpdateOrderStatus = async (status: OrderStatus) => {
        if (!id) return;

        const response = await updateOrderStatus(id as string, status);

        if (response.statusCode !== 200) {
            return;
        }
        getOrder();
    };

    async function handleRejectOrder(reject: Reject) {
        var response = await rejectOrder({ ...reject, orderId: id as string });
        if (response.statusCode !== 200) {
            toast.error("Failed to reject order");
            return;
        }

        getOrder();
    }

    function WidgetCard({
        title,
        className,
        children,
        childrenWrapperClass,
    }: {
        title?: string;
        className?: string;
        children: React.ReactNode;
        childrenWrapperClass?: string;
    }) {
        return (
            <div className={className}>
                <Title
                    as="h3"
                    className="mb-3.5 text-base font-semibold @5xl:mb-5 4xl:text-lg"
                >
                    {title}
                </Title>
                <div
                    className={cn(
                        "rounded-lg border border-muted px-5 @sm:px-7 @5xl:rounded-xl",
                        childrenWrapperClass,
                    )}
                >
                    {children}
                </div>
            </div>
        );
    }

    async function registerShipment(shipment: Shipment) {
        const response = await addShipment(order.id, shipment);
        if (response.statusCode !== 200) {
            toast.error("Failed to add track");
            return;
        }

        getOrder();
    }

    async function registerPurchaseOrder(purchaseOrder: PurchaseOrder) {
        const response = await addPurchaseOrder(order.id, {
            ...purchaseOrder,
            categoryId: (purchaseOrder.categoryId as any).value,
            weight: Number(purchaseOrder.weight),
            quantity: Number(purchaseOrder.quantity),
            price: Number(purchaseOrder.price),
            cubicMeter: Number(purchaseOrder.cubicMeter),
        });
        if (response.statusCode !== 200) {
            toast.error("Failed to add purchase order");
            return;
        }

        getOrder();
    }

    return (
        <>
            <PageHeader
                title={pageHeader.title}
                breadcrumb={pageHeader.breadcrumb}
            >
                <>
                    {role &&
                        (role.includes(
                            permissions.transactions.addTransaction,
                        ) ||
                            role.includes(permissions.system.admin)) && (
                            <div className="mt-6 flex items-center gap-4 @2xl:mt-0">
                                {order.status === OrderStatus.Requested && (
                                    <>
                                        <Button
                                            className="w-full gap-2 @lg:w-auto"
                                            variant="outline"
                                            onClick={() =>
                                                handleUpdateOrderStatus(
                                                    OrderStatus.Processing,
                                                )
                                            }
                                        >
                                            Start Processing
                                        </Button>
                                        <RejectModal
                                            show={false}
                                            handleSave={handleRejectOrder}
                                            title="Reject Order"
                                            description="Provide a reason for rejecting the order"
                                        />
                                    </>
                                )}
                                {order.status === OrderStatus.Processing && (
                                    <AddPurchaseOrderModal
                                        show={
                                            !(
                                                order.status ===
                                                OrderStatus.Processing
                                            )
                                        }
                                        handleSave={registerPurchaseOrder}
                                        categories={categories}
                                        title="Add Purchase Order"
                                        description="Add a new purchase order to the order"
                                    />
                                )}
                                {order.status === OrderStatus.Purchased && (
                                    <AddShipmentModal
                                        show={
                                            order.status !==
                                            OrderStatus.Purchased
                                        }
                                        handleSave={registerShipment}
                                        title="Add Tracking"
                                        description="Add a new track to the order"
                                    />
                                )}

                                {/* // add model  */}
                                {order.status === OrderStatus.InTransit ||
                                    (order.status == OrderStatus.Delivered && (
                                        <ModalButton
                                            label="Update Status"
                                            view={
                                                <div className="px-5 py-5">
                                                    <div className="modal-body">
                                                        <div className="mb-4">
                                                            <h3 className="text-lg font-semibold mt-2">
                                                                Update Status
                                                            </h3>
                                                            <p className="text-sm text-gray-500">
                                                                Select the
                                                                status to update
                                                                the order
                                                            </p>
                                                        </div>
                                                        <Select
                                                            options={[
                                                                {
                                                                    label: OrderStatus.InTransit,
                                                                    value: OrderStatus.Cancelled,
                                                                },
                                                            ]}
                                                            onClick={(e) =>
                                                                handleUpdateOrderStatus(
                                                                    e,
                                                                )
                                                            }
                                                        />
                                                    </div>
                                                    <div className="mt-3">
                                                        <Button
                                                            variant="outline"
                                                            color="secondary"
                                                            onClick={() =>
                                                                handleUpdateOrderStatus(
                                                                    OrderStatus.Cancelled,
                                                                )
                                                            }
                                                            className="w-full gap-2 @lg:w-auto"
                                                        >
                                                            Update
                                                        </Button>
                                                    </div>
                                                </div>
                                            }
                                        />
                                    ))}
                                {order.status === OrderStatus.InTransit && (
                                    <Button
                                        className="w-full gap-2 @lg:w-auto"
                                        variant="outline"
                                        onClick={() =>
                                            handleUpdateOrderStatus(
                                                OrderStatus.Delivered,
                                            )
                                        }
                                    >
                                        Complete Order
                                    </Button>
                                )}

                                {order.status === OrderStatus.Delivered && (
                                    <div className="flex flex-col justify-center">
                                        <Badge
                                            variant="outline"
                                            color="success"
                                            className="px-4 py-2 text-[13px] font-medium"
                                        >
                                            {order?.status}
                                        </Badge>
                                    </div>
                                )}
                                {order.status === OrderStatus.Rejected && (
                                    <div className="flex flex-col justify-center">
                                        <Badge
                                            variant="outline"
                                            color="danger"
                                            className="px-4 py-2 text-[13px] font-medium"
                                        >
                                            {order?.status} -
                                            {order?.rejectedReason}

                                        </Badge>
                                    </div>
                                )}
                            </div>
                        )}
                </>
            </PageHeader>

            <div className="mt-2 flex flex-col gap-y-6 @container sm:gap-y-10">
                <div className="flex gap-4">
                    <div className="w-2/3 flex flex-col justify-between">
                        <OrderSection order={order} />
                        <DetailsSection order={order} />
                    </div>
                    <div className="w-1/3 flex flex-col justify-between">
                        <WidgetCard
                            title="Order Status"
                            childrenWrapperClass="py-5 @5xl:py-8 flex"
                        >
                            <div className="ms-2 w-full space-y-7 border-s-2 border-gray-100">
                                {orderStatus.map((item) => (
                                    <div
                                        key={item.id}
                                        className={cn(
                                            "relative ps-6 text-sm font-medium before:absolute before:-start-[9px] before:top-px before:h-5 before:w-5 before:-translate-x-px before:rounded-full before:bg-gray-100 before:content-[''] after:absolute after:-start-px after:top-5 after:h-10 after:w-0.5 after:content-[''] last:after:hidden",
                                            currentOrderStatus > item.id
                                                ? "before:bg-green after:bg-green"
                                                : "after:hidden",
                                            currentOrderStatus === item.id &&
                                                "before:bg-green",
                                        )}
                                    >
                                        {currentOrderStatus >= item.id ? (
                                            <span className="absolute -start-1.5 top-1 text-white">
                                                <PiCheckBold className="h-auto w-3" />
                                            </span>
                                        ) : null}

                                        {item.label}
                                    </div>
                                ))}
                            </div>
                        </WidgetCard>
                    </div>
                </div>
                <TrackingHistory orderId={order.id} />
                <ShippingDetails order={order} refresh={getOrder} />
                <BillingTable orderId={order.id} />
                <FooterSection order={order} />
            </div>
        </>
    );
}
